import React from 'react';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as AdminIcon } from '../../../../../images/icons/assign_user.svg';
import {
  active,
  backgroundActive,
  inactive,
  lightBlack,
  lightGrey,
  lightTeal,
} from '../../../../../mainTheme';

const COLORS = {
  active: active,
  inactive: inactive,
  text: {
    primary: lightBlack,
    tertiary: lightGrey,
  },
  background: {
    active: backgroundActive,
    inactive: '#F6F6F9',
  },
} as const;

const StyledTypography: React.FC<{
  variant: 'title' | 'subtitle';
  children: React.ReactNode;
  sx?: any;
}> = ({ variant, children, sx, ...rest }) => {
  const styles = {
    title: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0.2px',
      color: COLORS.text.primary,
    },
    subtitle: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '22px',
      letterSpacing: '0.2px',
      color: COLORS.text.tertiary,
    },
  };

  return (
    <Typography sx={[styles[variant], sx]} {...rest}>
      {children}
    </Typography>
  );
};

const RadioOption: React.FC<{
  value: string;
  title: string;
  isSelected: boolean;
}> = ({ value, title, isSelected }) => (
  <Box
    sx={{
      border: '1px solid',
      borderRadius: '8px',
      borderColor: isSelected ? COLORS.active : COLORS.inactive,
      p: 1,
      mb: 1,
      backgroundColor: isSelected
        ? COLORS.background.active
        : COLORS.background.inactive,
      height: '48px',
      alignItems: 'center',
      display: 'flex',
    }}
  >
    <FormControlLabel
      value={value}
      sx={{
        margin: 0,
        width: '100%',
        justifyContent: 'space-between',
        '& .MuiFormControlLabel-label': { flex: 1 },
      }}
      control={
        <Radio
          sx={{
            '&.MuiRadio-root': {
              color: isSelected ? lightTeal : COLORS.text.tertiary,
            },
          }}
        />
      }
      labelPlacement="start"
      label={
        <Box>
          <StyledTypography variant="title" sx={{ ml: '8px', fontWeight: 500 }}>
            {title}
          </StyledTypography>
        </Box>
      }
    />
  </Box>
);

export const AdminConfirmation = ({
  onNext,
  onBack,
  mode = 'facility',
}: any) => {
  const [wantAdmin, setWantAdmin] = React.useState('yes');

  const radioOptions = [
    {
      value: 'yes',
      title: 'Yes',
    },
    {
      value: 'no',
      title: 'No',
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ProgressBar currentStep={2} totalSteps={2} />
      <AdminIcon />
      <Box>
        <StyledTypography variant="title">
          {mode === 'location'
            ? 'Do you want to assign a location admin?'
            : ' Do you want to assign a facility admin?'}
        </StyledTypography>
        <StyledTypography variant="subtitle">
          This requires user details.
        </StyledTypography>
      </Box>

      <RadioGroup
        value={wantAdmin}
        onChange={(e) => setWantAdmin(e.target.value)}
      >
        {radioOptions.map((option) => (
          <RadioOption
            key={option.value}
            {...option}
            isSelected={wantAdmin === option.value}
          />
        ))}
      </RadioGroup>

      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', mt: '16px' }}
      >
        <Button
          variant="text"
          color="secondary"
          onClick={onBack}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onNext(wantAdmin === 'yes')}
          disabled={!wantAdmin}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
